import JSZip from "jszip";

// Guard anti zip-bomb: l'anteprima è client-side e carica tutto in memoria.
export const ZIP_MAX_ENTRIES = 500;
export const ZIP_MAX_TOTAL_UNCOMPRESSED = 100 * 1024 * 1024; // 100 MB

export class ZipCorruptError extends Error {
  constructor() {
    super("zip.corrupt");
    this.name = "ZipCorruptError";
  }
}

export class ZipEncryptedError extends Error {
  constructor() {
    super("zip.encrypted");
    this.name = "ZipEncryptedError";
  }
}

export class ZipLimitExceededError extends Error {
  constructor() {
    super("zip.limit");
    this.name = "ZipLimitExceededError";
  }
}

export interface ZipEntryInfo {
  path: string;
  size: number;
}

export async function loadZip(bytes: ArrayBuffer): Promise<JSZip> {
  try {
    return await JSZip.loadAsync(bytes);
  } catch {
    throw new ZipCorruptError();
  }
}

export async function listZipEntries(bytes: ArrayBuffer): Promise<ZipEntryInfo[]> {
  const zip = await loadZip(bytes);
  const entries: ZipEntryInfo[] = Object.values(zip.files)
    .filter((f) => !f.dir)
    // JSZip non espone la dimensione non compressa nei tipi pubblici: la legge dai dati interni.
    // ponytail: se _data cambia forma il cap su dimensione degrada a 0 (resta il cap sul numero
    //   di voci); l'enforcement vero resta il controllo post-estrazione in extractZipEntry.
    .map((f) => ({ path: f.name, size: (f as any)._data?.uncompressedSize ?? 0 }));

  const total = entries.reduce((sum, e) => sum + e.size, 0);
  if (entries.length > ZIP_MAX_ENTRIES || total > ZIP_MAX_TOTAL_UNCOMPRESSED) {
    throw new ZipLimitExceededError();
  }
  return entries;
}

export async function extractZipEntry(bytes: ArrayBuffer, path: string): Promise<Uint8Array> {
  const zip = await loadZip(bytes);
  const entry = zip.file(path);
  if (!entry) throw new ZipCorruptError();
  let content: Uint8Array;
  try {
    content = await entry.async("uint8array");
  } catch (e) {
    if (/encrypted/i.test((e as Error)?.message ?? "")) throw new ZipEncryptedError();
    throw new ZipCorruptError();
  }
  // Enforcement reale del cap: l'estrazione è ciò che consuma memoria.
  if (content.byteLength > ZIP_MAX_TOTAL_UNCOMPRESSED) throw new ZipLimitExceededError();
  return content;
}
